import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { useUser } from '@clerk/react'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'

import {
	SettingsCard,
	SettingsCardTitle,
} from '@/components/common/settings-card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

const DeleteAccount = lazy(
	() => import('@/features/user/components/settings/delete-account'),
)
const ManageAccount = lazy(
	() => import('@/features/user/components/settings/manage-account'),
)
const NewPassword = lazy(
	() => import('@/features/user/components/settings/new-password'),
)

export const Route = createFileRoute('/_dashboard/settings/profile')({
	head: () => ({
		meta: [
			{
				title: 'Profile | BitLyft AIR®',
			},
		],
	}),

	component: ProfileRoute,
	errorComponent: RouteErrorComponent,
})

function ProfileRoute() {
	const { user } = useUser()

	return (
		<>
			<SettingsCard>
				<SettingsCardTitle>Manage Account</SettingsCardTitle>

				<Suspense
					fallback={
						<FormSkeleton firstLabel="First Name" secondLabel="Last Name" />
					}
				>
					<ManageAccount />
				</Suspense>
			</SettingsCard>

			{
				// TODO: Temporary solution to handle oAuth users
				user?.externalAccounts.length === 0 && (
					<SettingsCard className="flex-row items-start">
						<SettingsCardTitle>Change Password</SettingsCardTitle>

						<Suspense
							fallback={
								<FormSkeleton
									firstLabel="New Password"
									secondLabel="Confirm Password"
								/>
							}
						>
							<NewPassword />
						</Suspense>
					</SettingsCard>
				)
			}

			<SettingsCard className="flex-row items-start">
				<SettingsCardTitle>Delete Account</SettingsCardTitle>

				<Suspense fallback={<Skeleton className="h-10 w-full" />}>
					<DeleteAccount />
				</Suspense>
			</SettingsCard>
		</>
	)
}

/**
 * Form skeleton component for the profile page
 * @param firstLabel - The label for the first input
 * @param secondLabel - The label for the second input
 */
function FormSkeleton({
	firstLabel,
	secondLabel,
}: {
	firstLabel: string
	secondLabel: string
}) {
	return (
		<div className="flex w-full flex-col items-start justify-center gap-4 px-2">
			<div className="flex w-full flex-col items-start justify-start gap-2 sm:w-1/2">
				<div className="w-full">
					<Label className="mb-2">{firstLabel}</Label>
					<Skeleton className="mb-6 h-10 w-full" />
				</div>

				<div className="w-full">
					<Label className="mb-2">{secondLabel}</Label>
					<Skeleton className="mb-6 h-10 w-full" />
				</div>

				<Skeleton className="h-10 w-36" />
			</div>
		</div>
	)
}
